// src/components/billing/AdminBetaUsersPanel.tsx
/**
 * Admin Founders Panel - system-admin-only management of founder accounts.
 *
 * Founders (is_beta_user=True or BETA_ALLOWED_EMAILS) bypass all tier
 * restrictions and quota limits (except the hard spending ceiling).
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupIcon from "@mui/icons-material/Group";
import LockIcon from "@mui/icons-material/Lock";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useTranslation } from "../../i18n";
import api from "../../api/api";
import { athenaColors } from "../../theme/palette";
import logger from "../../utils/logger";
import { glass } from "../../theme/glassTokens";

interface BetaUser {
  id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  is_beta_user: boolean;
  from_settings: boolean;
}

interface SearchUser {
  id: number;
  email: string;
  username?: string;
  first_name?: string;
  last_name?: string;
}

const AdminBetaUsersPanel: React.FC = () => {
  const theme = useTheme();
  const textPrimary = glass.textPrimary;
  const textSecondary = glass.textSecondary;
  const { t } = useTranslation();
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [betaUsers, setBetaUsers] = useState<BetaUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Search state
  const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);

  const fetchBetaUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/billing/admin/beta-users/");
      setBetaUsers(response.data || []);
    } catch (err: any) {
      const msg =
        err.response?.data?.detail || "Failed to load beta users.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBetaUsers();
  }, [fetchBetaUsers]);

  // Debounced user search
  const handleSearch = useCallback(
    (query: string) => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
      if (query.length < 3) {
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }
      setSearchLoading(true);
      searchTimerRef.current = setTimeout(async () => {
        try {
          const response = await api.get(
            `/auth/users/search/?q=${encodeURIComponent(query)}`
          );
          setSearchResults(response.data || []);
        } catch (err) {
          logger.error("Error searching users:", err);
          setSearchResults([]);
        } finally {
          setSearchLoading(false);
        }
      }, 300);
    },
    []
  );

  const handleAddUser = useCallback(
    async (userOrEmail: SearchUser | string) => {
      setAdding(true);
      setError(null);
      try {
        const body =
          typeof userOrEmail === "string"
            ? { email: userOrEmail }
            : { user_id: userOrEmail.id };
        await api.post("/billing/admin/beta-users/add/", body);
        await fetchBetaUsers();
        setSearchResults([]);
      } catch (err: any) {
        const msg =
          err.response?.data?.detail || "Failed to add beta user.";
        setError(msg);
      } finally {
        setAdding(false);
      }
    },
    [fetchBetaUsers]
  );

  const handleRemoveUser = useCallback(
    async (userId: number) => {
      setRemovingId(userId);
      setError(null);
      try {
        await api.delete(`/billing/admin/beta-users/${userId}/remove/`);
        await fetchBetaUsers();
      } catch (err: any) {
        const msg =
          err.response?.data?.detail || "Failed to remove beta user.";
        setError(msg);
      } finally {
        setRemovingId(null);
      }
    },
    [fetchBetaUsers]
  );

  const getDisplayName = (user: BetaUser | SearchUser) => {
    const first = user.first_name || "";
    const last = user.last_name || "";
    const full = `${first} ${last}`.trim();
    return full || user.username || user.email;
  };

  const getInitials = (user: BetaUser | SearchUser) => {
    const name = getDisplayName(user);
    return name
      .split(" ")
      .map((w) => w[0])
      .slice(0, 2)
      .join("")
      .toUpperCase();
  };

  // Filter out users that are already beta
  const betaUserIds = new Set(betaUsers.map((u) => u.id));
  const filteredResults = searchResults.filter(
    (u) => !betaUserIds.has(u.id)
  );

  return (
    <Box sx={{ mt: 4 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${athenaColors.deepNavy}, ${athenaColors.royalBlue})`,
              display: "flex",
              alignItems: "center",
            }}
          >
            <GroupIcon sx={{ color: theme.palette.common.white, fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: textPrimary }}>
              {t("billing.beta_users.title", "Founders")}
            </Typography>
            <Typography variant="caption" sx={{ color: textSecondary }}>
              {t(
                "billing.beta_users.subtitle",
                "Manage founder accounts. Founders bypass tier restrictions and quotas (up to the spending ceiling)."
              )}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={t("billing.beta_users.count", "{count} users").replace(
            "{count}",
            String(betaUsers.length)
          )}
          size="small"
          sx={{
            fontWeight: 700,
            fontSize: "0.7rem",
            backgroundColor: alpha(athenaColors.royalBlue, 0.12),
            color: glass.accent,
            border: `1px solid ${alpha(athenaColors.royalBlue, 0.3)}`,
          }}
        />
      </Box>

      {error && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.light, 0.08),
            border: `1px solid ${alpha(theme.palette.error.light, 0.2)}`,
          }}
        >
          <Typography variant="body2" color="error.main">
            {error}
          </Typography>
        </Box>
      )}

      {/* Add user autocomplete */}
      <Box
        sx={{
          mb: 2,
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 2,
          background: glass.panel,
          border: `1px solid ${glass.panelBorder}`,
          backdropFilter: "blur(8px)",
          transition: "border-color 0.2s ease",
          "&:hover": { borderColor: glass.headerDivider },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
          <PersonAddIcon
            sx={{ color: athenaColors.blueGray, fontSize: 20 }}
          />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: textPrimary }}>
            {t("billing.beta_users.add_title", "Add Founder")}
          </Typography>
        </Box>
          <Autocomplete<SearchUser, false, false, true>
            freeSolo
            open={searchOpen}
            onOpen={() => setSearchOpen(true)}
            onClose={() => setSearchOpen(false)}
            options={filteredResults}
            loading={searchLoading || adding}
            getOptionLabel={(option) =>
              typeof option === "string"
                ? option
                : `${getDisplayName(option)} (${option.email})`
            }
            filterOptions={(x) => x}
            noOptionsText={t(
              "billing.beta_users.search_hint",
              "Type at least 3 characters to search..."
            )}
            onChange={(_e, value) => {
              if (!value) return;
              if (typeof value === "string") {
                const email = value.trim().toLowerCase();
                if (email.includes("@")) handleAddUser(email);
              } else {
                handleAddUser(value);
              }
            }}
            onInputChange={(_e, value, reason) => {
              if (reason === "input") {
                handleSearch(value);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                placeholder={t(
                  "billing.beta_users.search_placeholder",
                  "Search existing users or type a new email..."
                )}
                helperText={t(
                  "billing.beta_users.search_helper",
                  "Type at least 3 characters to search. Press Enter on a typed email to pre-register it."
                )}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {(searchLoading || adding) && (
                        <CircularProgress color="inherit" size={18} />
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                FormHelperTextProps={{
                  sx: { color: textSecondary, opacity: 0.7 },
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                key={option.id}
                sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
              >
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    fontSize: "0.75rem",
                    bgcolor: alpha(athenaColors.royalBlue, 0.15),
                    color: glass.accent,
                  }}
                >
                  {getInitials(option)}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {getDisplayName(option)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: textSecondary }}>
                    {option.email}
                  </Typography>
                </Box>
              </Box>
            )}
          />
      </Box>

      {/* Current beta users list */}
      <Box
        sx={{
          p: { xs: 1.5, sm: 2.5 },
          borderRadius: 2,
          background: glass.panel,
          border: `1px solid ${glass.panelBorder}`,
          backdropFilter: "blur(8px)",
          transition: "border-color 0.2s ease",
          "&:hover": { borderColor: glass.headerDivider },
        }}
      >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 3,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : betaUsers.length === 0 ? (
            <Typography
              variant="body2"
              sx={{ textAlign: "center", py: 3, color: textSecondary }}
            >
              {t("billing.beta_users.empty", "No beta users configured.")}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {betaUsers.map((user) => (
                <Box
                  key={user.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    borderRadius: 2,
                    bgcolor: glass.rowDivider,
                    border: `1px solid ${glass.rowDivider}`,
                    transition: "background-color 0.15s",
                    "&:hover": {
                      bgcolor: glass.rowDivider,
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: "0.8rem",
                      bgcolor: alpha(athenaColors.royalBlue, 0.15),
                      color: glass.accent,
                    }}
                  >
                    {getInitials(user)}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      noWrap
                    >
                      {getDisplayName(user)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: textSecondary }}
                      noWrap
                    >
                      {user.email}
                    </Typography>
                  </Box>
                  {user.from_settings && (
                    <Tooltip
                      title={t(
                        "billing.beta_users.from_settings_tooltip",
                        "This user is in the BETA_ALLOWED_EMAILS settings list and cannot be removed from the UI."
                      )}
                    >
                      <LockIcon
                        sx={{
                          fontSize: 16,
                          color: glass.textAbsent,
                        }}
                      />
                    </Tooltip>
                  )}
                  {!user.from_settings && (
                    <Tooltip
                      title={t(
                        "billing.beta_users.remove_tooltip",
                        "Remove beta access"
                      )}
                    >
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveUser(user.id)}
                        disabled={removingId === user.id}
                        sx={{
                          color: glass.textSecondary,
                          "&:hover": {
                            color: "error.main",
                            bgcolor: alpha(
                              theme.palette.error.light,
                              0.08
                            ),
                          },
                        }}
                      >
                        {removingId === user.id ? (
                          <CircularProgress size={16} />
                        ) : (
                          <DeleteOutlineIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              ))}
            </Box>
          )}
      </Box>
    </Box>
  );
};

export default AdminBetaUsersPanel;
